import React, { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { ArrowsExpandIcon } from '@heroicons/react/outline';
import { DeleteOutlineRounded } from '@mui/icons-material';
import { CircularProgress, Tooltip } from '@mui/material';
import cn from 'classnames';
import isEqual from 'lodash.isequal';
import differenceWith from 'lodash/differenceWith';

import AnalysisCategoryPlotContentView from '@components/analysisCategories/AnalysisCategoryPlotContentView';
import { CustomPlotStylingOptions } from '@components/analysisCategories/comparative/plots/PlotlyVolcanoPlotUtil';
import Button from '@components/Button';
import Mono from '@components/elements/Mono';
import MethodsModal from '@components/experiments/methods/MethodsModal';
import ResultsModal from '@components/experiments/ResultsModal';
import NoSsr from '@components/NoSsr';
import PlotContextMenuButton from '@components/plots/PlotContextMenuButton';
import PlotDataErrorView from '@components/plots/PlotDataErrorView';
import PlotLegendView from '@components/plots/PlotLegendView';
import PlotShellView from '@components/plots/PlotShellView';
import PlotTitle from '@components/plots/PlotTitle';
import { initialMouseState, MousePosition } from '@components/plots/PlotTypes';
import { isDoubleClickEnabled, isDoubleWidePlot, isRightClickEnabled } from '@components/plots/PlotUtil';
import PlutoErrorBoundary from '@components/PlutoErrorBoundary';

import { AuthContext } from '@contexts/AuthContext';
import { PlotOverrideSummary, useOptionalExperimentDetailViewContext } from '@contexts/ExperimentDetailViewContext';
import { useFeatureToggleContext } from '@contexts/FeatureToggleContext';
import { PlotContextProvider } from '@contexts/PlotContext';
import { ResizableContainerContextProvider } from '@contexts/ResizableContainerContext';

import { usePlotThumbnail } from '@hooks/generateAndSaveThumbnail';
import useAnalysisParameters from '@hooks/useAnalysisParameters';
import useDebouncedResizeObserver from '@hooks/useDebouncedResizeObserver';
import useExperimentPermissions from '@hooks/useExperimentPermissions';
import useOrganizationPermissions from '@hooks/useOrganizationPermissions';
import usePlot from '@hooks/usePlot';
import usePlotLegendItems from '@hooks/usePlotLegendItems';

import { AnalysisShortname } from '@models/analysis/AnalysisType';
import { ExperimentAnalysisWithCustomLegend, isPipelineAnalysis } from '@models/analysis/ExperimentAnalysis';
import { PrismPreviewGraph } from '@models/analysis/PrismAnalysis';
import Experiment from '@models/Experiment';
import { PipelineStatusPlotData, PlotPipelinePlotData } from '@models/ExperimentData';
import Plot, { supportedThumbnailDisplayTypes } from '@models/Plot';
import { ThemeStyle } from '@models/PlotConfigs';
import PrismGraphsetDisplayOption from '@models/plotDisplayOption/PrismDisplayOption';

import { ApiError } from '@services/ApiError';

import { updateHashWithPlotUUID } from '@util/hashUtil';
import LocalStorageService from '@util/LocalStorageService';
import Logger from '@util/Logger';
import { hasCommentsPermission } from '@util/PermissionUtil';
import { isBlank } from '@util/StringUtil';
import { isDefined } from '@util/TypeGuards';

import ConditionalWrapper from '../ConditionalWrapper';
import { validateAnalysisInputsPlotDataReady } from '../experiments/analyses/inputs/AnalysisInputFormUtil';
import WhiteIconButton from '../WhiteIconButton';
import CustomPlotLegendView from './CustomPlotLegendView';
import PlotDisplayLoadingView from './PlotDisplayLoadingView';
import PlotImageThumbnail from './PlotImageThumbnail';

const logger = Logger.make('PlotView');

const arePropsEqual = (prevProps: Props, nextProps: Props): boolean => {
    const timestamp = new Date().toISOString();

    const logChange = (label: string, prev: any, next: any) => {
        logger.info(`[${timestamp}] ${label} changed:`, {
            prev,
            next,
            differences: {
                added: differenceWith(next, prev, isEqual),
                removed: differenceWith(prev, next, isEqual),
            },
        });
    };

    // Check experiment user permissions
    if (!isEqual(prevProps.experiment?.user_permissions, nextProps.experiment?.user_permissions)) {
        logChange('user_permissions', prevProps.experiment?.user_permissions, nextProps.experiment?.user_permissions);
    }

    // Check experiment props
    if (!isEqual(prevProps.experiment, nextProps.experiment)) {
        logChange('experiment', prevProps.experiment, nextProps.experiment);
    }

    // Check customPlotStylingOptions prop
    if (!isEqual(prevProps.customPlotStylingOptions, nextProps.customPlotStylingOptions)) {
        logChange('customPlotStylingOptions', prevProps.customPlotStylingOptions, nextProps.customPlotStylingOptions);
    }

    // Check other props
    if (prevProps.plotListID !== nextProps.plotListID) {
        logger.info(`[${timestamp}] plotListID changed:`, {
            prev: prevProps.plotListID,
            next: nextProps.plotListID,
        });
    }

    if (prevProps.isDragging !== nextProps.isDragging) {
        logger.info(`[${timestamp}] isDragging changed:`, {
            prev: prevProps.isDragging,
            next: nextProps.isDragging,
        });
    }

    if (prevProps.showExpand !== nextProps.showExpand) {
        logger.info(`[${timestamp}] showExpand changed:`, {
            prev: prevProps.showExpand,
            next: nextProps.showExpand,
        });
    }

    if (prevProps.publicationMode !== nextProps.publicationMode) {
        logger.info(`[${timestamp}] publicationMode changed:`, {
            prev: prevProps.publicationMode,
            next: nextProps.publicationMode,
        });
    }

    // Memoization comparison
    return (
        isEqual(prevProps.experiment, nextProps.experiment) &&
        isEqual(prevProps.customPlotStylingOptions, nextProps.customPlotStylingOptions) &&
        prevProps.plotListID === nextProps.plotListID &&
        prevProps.isDragging === nextProps.isDragging &&
        prevProps.showExpand === nextProps.showExpand &&
        prevProps.publicationMode === nextProps.publicationMode
    );
};

export type LegendPosition = 'bottom' | 'right' | 'auto';
export type Props = {
    aspectRatio?: number | null;
    cursorClassName?: string;
    customPlotStylingOptions?: CustomPlotStylingOptions | null;
    disableDownloadLinks?: boolean;
    disableFullWidth?: boolean;
    disableMaxHeight?: boolean;
    disablePlotImages?: boolean;
    disableRoundedCorners?: boolean;
    disableSelected?: boolean;
    experiment?: Experiment | null;
    extraPadding?: boolean;
    hideEditButton?: boolean;
    hideLegend?: boolean;
    hideMoreMenu?: boolean;
    hideTitle?: boolean;
    isDragging?: boolean;
    isExportMode?: boolean;
    isLinkedExperiment?: boolean;
    legendPosition?: LegendPosition;
    minHeight?: number;
    noPadding?: boolean;
    overrides?: PlotOverrideSummary | null;
    plot?: Plot;
    plotListID?: string;
    publicationMode?: boolean;
    publicKey?: string | null;
    resizeNoLeading?: boolean;
    resizeNoTrailing?: boolean;
    setDragEnabled?: (enabled: boolean) => void;
    showExpand?: boolean;
    showExperimentLink?: boolean;
    xAxisTruncated?: boolean;
};

const PlotView = ({
    cursorClassName,
    customPlotStylingOptions = null,
    disableDownloadLinks,
    disableFullWidth = false,
    disableMaxHeight = false,
    disablePlotImages = false,
    disableRoundedCorners = false,
    disableSelected = false,
    experiment,
    extraPadding,
    hideEditButton,
    hideLegend = false,
    hideMoreMenu = false,
    hideTitle = false,
    isDragging = false,
    isExportMode = false,
    isLinkedExperiment = false,
    legendPosition = 'auto',
    noPadding = false,
    overrides: overridesParam,
    plot: plotParam,
    plotListID,
    publicationMode,
    publicKey,
    setDragEnabled,
    showExpand = false,
    showExperimentLink,
    xAxisTruncated = false,
}: Props) => {
    const plotId = plotListID ?? plotParam?.uuid ?? '';
    const featureToggles = useFeatureToggleContext();
    const [methodsOpen, setMethodsOpen] = React.useState(false);
    const [resultsOpen, setResultsOpen] = React.useState(false);
    const [isPlotFullyRendered, setIsPlotFullyRendered] = useState(false);
    const [plotThumbnailError, setPlotThumbnailError] = useState(false);
    const [legendLoaded, setLegendLoaded] = useState(false);
    const [isRefreshingThumbnail, setIsRefreshingThumbnail] = useState(false);
    const plotExportRef = useRef<HTMLDivElement | null>(null);
    const [mousePosition, setMousePosition] = useState<MousePosition>(initialMouseState());
    const [showLegendBottom, setShowLegendBottom] = useState(legendPosition === 'bottom');
    const permissions = useExperimentPermissions(experiment, !!publicKey);
    const { features } = useOrganizationPermissions();
    const { user } = useContext(AuthContext);

    const plotContainerRef = useRef<HTMLDivElement | null>(null);
    const plotThumbnailRef = useRef<HTMLDivElement | null>(null);
    const { size: plotContainerSize } = useDebouncedResizeObserver({ ref: plotContainerRef, useDebounce: true });
    const { ref: plotSizeRef, size } = useDebouncedResizeObserver({ wait: 200, useDebounce: true });

    const { plot, plotData, plotDataError, plotDataLoading, isPlotDataValidating, mutatePlot, plotLoading } = usePlot({
        experiment,
        plot: plotParam,
        plotId,
        overrides: overridesParam,
        publicKey,
        useThumbnailImage: !disablePlotImages && !isRefreshingThumbnail,
    });

    const displaySupportsThumbnails = supportedThumbnailDisplayTypes.includes(plot?.display?.display_type || '');
    const thumbnailUri = plot?.thumbnail_uri?.rectangle ?? null;
    const isUsingThumbnail =
        !disablePlotImages && displaySupportsThumbnails && !!thumbnailUri && !isRefreshingThumbnail;

    const { analysisParameters } = useAnalysisParameters({ plot, experiment, publicKey });
    const {
        style: legendStyle,
        legendItems,
        horizontalOnly,
    } = usePlotLegendItems({ plot, plotData, experiment, publicKey });

    const {
        clearPlotOverrides,
        getPlotOverrides,
        handleChangeSelectedPlot,
        plotDisplayLoadingStates,
        selectedPlot,
        setCurrentContextMenuPlotId,
        setCurrentPlotPanel,
        setExperimentModalOpen,
        setOpenOnComments,
        setOpenOnPlot,
    } = useOptionalExperimentDetailViewContext();

    const graphs: PrismPreviewGraph[] = analysisParameters?.preview_graphs ?? [];

    const overrides: PlotOverrideSummary = getPlotOverrides?.(plotId) ?? { hasOverrides: false };
    const isSelected = !disableSelected && plotId === selectedPlot?.uuid;
    const loading = isUsingThumbnail
        ? !!plot?.analysis && (plotLoading || plotDataLoading || plotData?.pipeline_status === 'in_progress')
        : !!plot?.analysis && (plotDataLoading || plotData?.pipeline_status === 'in_progress');

    const hasValidAnalysisInputs =
        plot?.analysis_form_type === 'analysis_input' ? validateAnalysisInputsPlotDataReady(plot.analysis) : true;

    const sourceExperimentReady = isDefined(plot?.analysis?.uuid);
    const linkedExperimentReady = isDefined(experiment?.uuid);
    const experimentReady =
        (isLinkedExperiment ? linkedExperimentReady : sourceExperimentReady) &&
        !plotDataLoading &&
        hasValidAnalysisInputs;
    const thumbnailReady = !plotLoading && isDefined(plot?.analysis?.uuid) && hasValidAnalysisInputs;
    const dataReady = isUsingThumbnail ? thumbnailReady : experimentReady;

    const pipelineProcessing = (plotData as PipelineStatusPlotData)?.pipeline_status === 'in_progress';
    const hasPipelinePlotData =
        ((plotData as PipelineStatusPlotData)?.items ?? []).length > 0 ||
        (plotData?.count ?? 0) > 0 ||
        Boolean((plotData as PlotPipelinePlotData)?.plots?.images);

    const showPlotTitle = isUsingThumbnail
        ? !hideTitle
        : !hideTitle && !pipelineProcessing && (!isPipelineAnalysis(plot?.analysis) || hasPipelinePlotData);
    const showLegend = !hideLegend && !pipelineProcessing && plotData?.count !== 0;

    const handleLegendLoad = () => {
        setLegendLoaded(true);
    };

    useEffect(() => {
        logger.info('PlotView render:', {
            timestamp: new Date().toISOString(),
            experiment,
            plotListID,
            isDragging,
            showExpand,
        });
    }, [experiment, plotListID, isDragging, showExpand]);

    useEffect(() => {
        if (legendPosition === 'bottom' || plot?.display?.display_type === 'enrichment_plot') {
            setShowLegendBottom(true);
        } else {
            setShowLegendBottom(false);
        }
    }, [legendPosition, size.width, plot?.display?.display_type]);

    useEffect(() => {
        if (!methodsOpen && !resultsOpen) return setDragEnabled?.(true);
        setDragEnabled?.(false);
    }, [methodsOpen, resultsOpen]);

    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (LocalStorageService.isDevMode()) {
            return;
        }
        event.preventDefault();
        setMousePosition({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
        });
    };

    const handlePlotSelected = () => {
        if (selectedPlot && selectedPlot?.uuid === plot?.uuid) {
            handleChangeSelectedPlot?.(null);
            setExperimentModalOpen?.(false);
        } else if (plot) {
            handleChangeSelectedPlot?.(plot);
            setExperimentModalOpen?.(true);
        }

        updateHashWithPlotUUID(selectedPlot?.uuid || plot?.uuid);
    };

    const tokenIsInvalid = () => {
        if (!plotDataError) return false;
        if (plotDataError.code === 'token_not_valid') return true;
        return false;
    };

    const commentsEnabled = hasCommentsPermission({ features, experiment, user });
    const expandPlotFullScreen = () => {
        if (plot) {
            handleChangeSelectedPlot?.(plot);
            if (commentsEnabled && permissions.canEdit) {
                // Open comments if permissions allow
                setOpenOnComments?.(true);
            } else {
                // Else open plot fullscreen
                setOpenOnPlot?.(true);
            }
            setCurrentPlotPanel?.(plot.analysis?.uuid ? 'plot' : 'analysis');
            setExperimentModalOpen?.(true);
            updateHashWithPlotUUID(plot.uuid);
        }
    };

    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash;
            const isLegacyHash = hash === `#plot-${plot?.uuid}`;

            if (isLegacyHash) {
                expandPlotFullScreen();
                return;
            }

            const hashParams = new URLSearchParams(hash.replace('#', ''));
            const plotUUID = hashParams.get('plot');
            const commentUUID = hashParams.get('comment');

            if (commentUUID && plotUUID === plot?.uuid) {
                setOpenOnComments?.(true);
                expandPlotFullScreen();
                return;
            }

            // Deep link to plot only
            if (plotUUID === plot?.uuid) {
                expandPlotFullScreen();
            }
        };

        handleHashChange();

        window.addEventListener('hashchange', handleHashChange);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [plot?.uuid]);

    const isDoubleWide = isDoubleWidePlot(plot);
    const subtitle = graphs.find((g) => g.id === (plot?.display as PrismGraphsetDisplayOption)?.selected_graph)?.title;

    const { generateAndSaveThumbnail, loadingPlotThumbnailGeneration } = usePlotThumbnail({
        containerRef: plotThumbnailRef,
        experiment,
        plot,
        onComplete: () => {
            mutatePlot();
            if (isRefreshingThumbnail) {
                setIsRefreshingThumbnail(false);
            }
        },
    });

    const noThumbnailExists = !thumbnailUri;
    const plotHasLoaded =
        isPlotFullyRendered && !loading && dataReady && legendLoaded && !pipelineProcessing && !isPlotDataValidating;
    useEffect(() => {
        if (!plotHasLoaded || (!noThumbnailExists && !isRefreshingThumbnail)) {
            setIsPlotFullyRendered(false);
            return;
        }

        if (
            (noThumbnailExists || isRefreshingThumbnail) &&
            !disablePlotImages &&
            displaySupportsThumbnails &&
            plotHasLoaded
        ) {
            generateAndSaveThumbnail();
        }
    }, [noThumbnailExists, plotHasLoaded, isRefreshingThumbnail]);

    const refreshThumbnail = () => {
        if (!disablePlotImages && displaySupportsThumbnails) {
            setIsRefreshingThumbnail(true);
            return;
        }
        alert('Failed to refresh thumbnail. Please try again later or contact support if the issue persists.');
    };

    const renderTooltipWrapper = useCallback((children: JSX.Element | JSX.Element[]) => {
        return (
            <Tooltip
                title="Open to view interactive plot"
                placement="top"
                enterDelay={1_000}
                enterNextDelay={1_000}
                followCursor
            >
                <div className="w-full h-full">{children}</div>
            </Tooltip>
        );
    }, []);

    if (!!plotDataError)
        return (
            <div className="relative h-full w-full rounded-2xl bg-white" data-cy="plot-card">
                <PlotDataErrorView
                    error={ApiError.getMessage(plotDataError)}
                    code={plotDataError?.code}
                    plot={plot ?? plotParam}
                    experiment={experiment}
                />
            </div>
        );
    if (!dataReady)
        return (
            <div
                className="relative flex h-full w-full items-center justify-center rounded-2xl bg-white"
                data-cy="plot-card"
            >
                <PlotShellView plot={plot} loading={loading} experiment={experiment} hideEditButton={hideEditButton} />
            </div>
        );

    if (!experiment || !plot) return;

    return (
        <NoSsr>
            <PlotContextProvider
                experiment={experiment}
                analysisParameters={analysisParameters}
                plot={plot}
                plotId={plotId}
                hideEditButton={hideEditButton}
                plotData={plotData}
                plotDataError={plotDataError}
                plotDataLoading={plotDataLoading}
                isPlotDataValidating={isPlotDataValidating}
                overrides={overrides}
                setDragEnabled={(enabled) => setDragEnabled?.(enabled)}
                publicationMode={publicationMode}
                isDragging={isDragging}
                isExportMode={isExportMode}
                disableMaxHeight={disableMaxHeight}
                mutatePlot={mutatePlot}
                isEditing={isSelected}
            >
                <ConditionalWrapper condition={isUsingThumbnail} wrapper={renderTooltipWrapper}>
                    <div
                        className={cn('group relative h-full w-full bg-white', cursorClassName, {
                            'ring-4 ring-indigo-500': isSelected,
                            'md:col-span-2': isDoubleWide && !publicationMode,
                            'cursor-pointer': isBlank(cursorClassName) && isDoubleClickEnabled(plot),
                            'rounded-2xl': !disableRoundedCorners,
                            'overflow-hidden': plot?.analysis_type === 'image',
                            'pb-8 pt-4': !noPadding,
                        })}
                        data-cy="plot-card"
                        data-plot-id={plot?.uuid}
                        onContextMenu={isRightClickEnabled(plot) ? handleRightClick : undefined}
                        onDoubleClick={isDoubleClickEnabled(plot) ? handlePlotSelected : undefined}
                    >
                        {((isPlotDataValidating && !loading) || tokenIsInvalid()) && (
                            <div className={'absolute left-4 top-4 flex items-center space-x-1'}>
                                <CircularProgress size={12} />
                                <span className="text-xs text-default"></span>
                            </div>
                        )}
                        {!hideMoreMenu && (
                            <PlotContextMenuButton
                                plot={plot ?? plotParam}
                                plotData={plotData}
                                experiment={experiment}
                                setMethodsOpen={setMethodsOpen}
                                setResultsOpen={setResultsOpen}
                                ready={dataReady}
                                mousePosition={mousePosition}
                                setMousePosition={setMousePosition}
                                hideEditButton={hideEditButton}
                                showExperimentLink={showExperimentLink}
                                disableDownloadLinks={disableDownloadLinks}
                                onMenuOpen={() => {
                                    setCurrentContextMenuPlotId?.(plot?.uuid ?? null);
                                }}
                                onMenuClose={() => {
                                    setCurrentContextMenuPlotId?.(null);
                                }}
                                publicKey={publicKey}
                                refreshThumbnail={refreshThumbnail}
                            />
                        )}
                        <div
                            ref={plotSizeRef}
                            className={cn('h-full ', {
                                'md:px-8 lg:px-16': extraPadding && !noPadding,
                                'px-4 sm:px-6': plot?.analysis_type !== 'text' && !noPadding,
                                'px-0': plot?.analysis_type === 'text',
                            })}
                        >
                            <div className="flex h-full flex-grow flex-col-reverse">
                                <div
                                    className={cn('flex h-full flex-col space-y-2 ', {
                                        'justify-between':
                                            dataReady &&
                                            plot?.display?.display_type !== 'image' &&
                                            !pipelineProcessing &&
                                            showPlotTitle,
                                        'justify-center': !dataReady || pipelineProcessing || !showPlotTitle,
                                        'pb-4': overrides.hasOverrides,
                                    })}
                                >
                                    {dataReady && (
                                        <div ref={plotExportRef} className="flex h-full max-h-full w-full flex-col">
                                            {plotDisplayLoadingStates?.[plotId] ? (
                                                <PlotDisplayLoadingView />
                                            ) : (
                                                <>
                                                    {showPlotTitle && (
                                                        <PlotTitle
                                                            plot={plot}
                                                            className="px-10"
                                                            publicationMode={publicationMode}
                                                            subTitle={subtitle}
                                                            linkedExperimentName={
                                                                isLinkedExperiment ? experiment?.name : null
                                                            }
                                                            customPlotStylingOptions={customPlotStylingOptions}
                                                        />
                                                    )}
                                                    {isUsingThumbnail ? (
                                                        <div className="relative flex h-full w-full overflow-hidden">
                                                            <PlotImageThumbnail
                                                                src={(thumbnailUri as string) || ''}
                                                                alt={`Thumbnail for plot ${plotId}`}
                                                                onError={() => {
                                                                    setPlotThumbnailError(true);
                                                                }}
                                                                error={plotThumbnailError}
                                                            />
                                                            {showExpand && !loadingPlotThumbnailGeneration && (
                                                                <span className="absolute right-2 top-2 z-50 hidden opacity-0 transition-all duration-200 ease-in-out group-hover:inline-block group-hover:opacity-100">
                                                                    <WhiteIconButton
                                                                        onClick={expandPlotFullScreen}
                                                                        color="primary"
                                                                    >
                                                                        <ArrowsExpandIcon width={24} />
                                                                    </WhiteIconButton>
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div
                                                                ref={plotThumbnailRef}
                                                                className={cn(
                                                                    'relative flex h-full w-full overflow-hidden',

                                                                    {
                                                                        'flex-col': showLegendBottom || horizontalOnly,
                                                                    },
                                                                )}
                                                            >
                                                                {showExpand && !loadingPlotThumbnailGeneration && (
                                                                    <span className="absolute right-2 top-2 z-50 hidden opacity-0 transition-all duration-200 ease-in-out group-hover:inline-block group-hover:opacity-100">
                                                                        <WhiteIconButton
                                                                            onClick={expandPlotFullScreen}
                                                                            color="primary"
                                                                        >
                                                                            <ArrowsExpandIcon width={24} />
                                                                        </WhiteIconButton>
                                                                    </span>
                                                                )}
                                                                <div
                                                                    className={cn(
                                                                        'relative flex h-full w-full justify-center overflow-hidden',
                                                                        {
                                                                            'border-2 border-dashed border-red-500':
                                                                                featureToggles.isEnabled(
                                                                                    'plot_size_debug',
                                                                                ),
                                                                        },
                                                                    )}
                                                                    ref={plotContainerRef}
                                                                >
                                                                    {featureToggles.isEnabled('plot_size_debug') && (
                                                                        <div className="pointer-events-none absolute z-50 flex h-full w-full flex-col items-start justify-end">
                                                                            <div className="z-50 bg-red-500/50 p-2 text-xs">
                                                                                <p>
                                                                                    <Mono>PlotView.tsx</Mono>
                                                                                </p>
                                                                                <p>
                                                                                    height: {plotContainerSize?.height}
                                                                                    px
                                                                                </p>
                                                                                <p>
                                                                                    width: {plotContainerSize?.width}px
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    <ResizableContainerContextProvider
                                                                        containerRef={plotContainerRef}
                                                                        size={plotContainerSize}
                                                                    >
                                                                        <PlutoErrorBoundary>
                                                                            <AnalysisCategoryPlotContentView
                                                                                customPlotStylingOptions={
                                                                                    customPlotStylingOptions
                                                                                }
                                                                                zoomEnabled={isExportMode}
                                                                                xAxisTruncated={xAxisTruncated}
                                                                                setIsPlotFullyRendered={
                                                                                    setIsPlotFullyRendered
                                                                                }
                                                                                loadingPlotThumbnailGeneration={
                                                                                    loadingPlotThumbnailGeneration
                                                                                }
                                                                            />
                                                                        </PlutoErrorBoundary>
                                                                    </ResizableContainerContextProvider>
                                                                </div>

                                                                {legendItems.length > 0 && showLegend && (
                                                                    <div
                                                                        className={cn('mt-4', {
                                                                            'max-w-[30%] flex-shrink-0':
                                                                                !showLegendBottom && !horizontalOnly,
                                                                        })}
                                                                    >
                                                                        <PlotLegendView
                                                                            items={legendItems}
                                                                            style={legendStyle ?? ThemeStyle.outline}
                                                                            horizontal={
                                                                                showLegendBottom || horizontalOnly
                                                                            }
                                                                            fullWidth={
                                                                                !disableFullWidth &&
                                                                                (plot?.display?.is_full_width ||
                                                                                    publicationMode)
                                                                            }
                                                                            onLoad={handleLegendLoad}
                                                                            customPlotStylingOptions={
                                                                                customPlotStylingOptions
                                                                            }
                                                                        />
                                                                    </div>
                                                                )}

                                                                {showLegend &&
                                                                    (
                                                                        plot?.analysis as ExperimentAnalysisWithCustomLegend
                                                                    )?.pipeline_status === 'completed' && (
                                                                        <div
                                                                            className={cn('mt-4', {
                                                                                'max-w-[30%] flex-shrink-0':
                                                                                    !showLegendBottom &&
                                                                                    !horizontalOnly,
                                                                            })}
                                                                        >
                                                                            <CustomPlotLegendView
                                                                                plot={plot}
                                                                                shortname={
                                                                                    plot?.analysis
                                                                                        ?.analysis_type as AnalysisShortname
                                                                                }
                                                                                plotData={
                                                                                    plotData as PipelineStatusPlotData
                                                                                }
                                                                                showLegendBottom={showLegendBottom}
                                                                                publicationMode={publicationMode}
                                                                                isExportMode={isExportMode}
                                                                                onLoad={handleLegendLoad}
                                                                                customPlotStylingOptions={
                                                                                    customPlotStylingOptions
                                                                                }
                                                                            />
                                                                        </div>
                                                                    )}
                                                            </div>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                    {!dataReady && (
                                        <div className="h-full w-full">
                                            <PlotShellView
                                                plot={plot}
                                                loading={loading}
                                                experiment={experiment}
                                                hideEditButton={hideEditButton}
                                            />
                                        </div>
                                    )}
                                </div>
                                {dataReady && overrides.hasOverrides && (
                                    <div className="absolute bottom-0 left-0 flex-shrink-0 px-2 py-2 text-red-800">
                                        <Button
                                            color="inherit"
                                            startIcon={<DeleteOutlineRounded />}
                                            variant="text"
                                            onClick={() => clearPlotOverrides?.(plotId)}
                                            size="small"
                                        >
                                            Discard Changes
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </ConditionalWrapper>
                {plot?.analysis && experiment && (
                    <>
                        <MethodsModal
                            open={methodsOpen}
                            onClose={() => setMethodsOpen(false)}
                            plot={plot}
                            experiment={experiment}
                            overrideAnalysisId={overrides.analysis_id}
                            publicKey={publicKey}
                        />
                        <ResultsModal
                            open={resultsOpen}
                            onClose={() => setResultsOpen(false)}
                            plot={plot}
                            experiment={experiment}
                        />
                    </>
                )}
            </PlotContextProvider>
        </NoSsr>
    );
};

export default memo(PlotView, arePropsEqual);
